<script setup lang="ts">
import { Link, router, useForm } from "@inertiajs/vue3";
import { useDebounceFn } from "@vueuse/core";
import {
    ArrowLeft,
    ExternalLink,
    Globe2,
    LayoutPanelTop,
    Monitor,
    Share,
    Smartphone,
} from "lucide-vue-next";
import { computed, nextTick, onBeforeUnmount, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import route from "ziggy-js";
import AppLayout from "@/Layouts/AppLayout.vue";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import AvatarUpload from "@/components/ui/avatar-upload/AvatarUpload.vue";
import type { IAppearance } from "@/types/IAppearance";
import type {
    BookingPageEditorProps,
    BookingPageState,
} from "@/types/BookingPage";
import AppearancePicker from "../Appearances/components/AppearancePicker.vue";
import PaidFeatureIcon from "../Atomic/PaidFeatureIcon.vue";
import EventTypesConfiguration from "../EventTypes/grid/EventTypesConfiguration.vue";
import ShareHubGroupModal from "../EventTypes/components/ShareHub/ShareHubGroupModal.vue";
import BookingPagePreview from "./components/BookingPagePreview.vue";

const props = defineProps<BookingPageEditorProps>();
const { t } = useI18n();
const desktopPreviewOpen = ref(false);
const desktopPreviewExpanded = ref(false);
const desktopPanelTransform = ref("");
const previewCard = ref<HTMLElement>();
const expandedPanel = ref<HTMLElement>();
let closeTimeout: number | undefined;
const saveStatus = ref<"idle" | "saving" | "saved" | "error">("idle");

function collapsedTransform(): string {
    const source = previewCard.value?.getBoundingClientRect();
    const target = expandedPanel.value?.getBoundingClientRect();

    if (!source || !target || !target.width || !target.height) return "";

    const dx = source.left - target.left;
    const dy = source.top - target.top;

    return `translate(${dx}px, ${dy}px) scale(${source.width / target.width}, ${source.height / target.height})`;
}

function openDesktopPreview(): void {
    window.clearTimeout(closeTimeout);
    desktopPreviewOpen.value = true;
    desktopPreviewExpanded.value = false;

    nextTick(() => {
        desktopPanelTransform.value = collapsedTransform();

        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                desktopPreviewExpanded.value = true;
                desktopPanelTransform.value = "";
            });
        });
    });
}

function closeDesktopPreview(): void {
    if (!desktopPreviewOpen.value) return;

    desktopPanelTransform.value = collapsedTransform();
    desktopPreviewExpanded.value = false;
    closeTimeout = window.setTimeout(() => {
        desktopPreviewOpen.value = false;
    }, 300);
}

function onPreviewKeydown(event: KeyboardEvent): void {
    if (event.key === "Escape") closeDesktopPreview();
}

watch(desktopPreviewOpen, (open) => {
    if (open) {
        window.addEventListener("keydown", onPreviewKeydown);
    } else {
        window.removeEventListener("keydown", onPreviewKeydown);
    }
});

onBeforeUnmount(() => {
    window.clearTimeout(closeTimeout);
    window.removeEventListener("keydown", onPreviewKeydown);
});

const form = useForm({
    title: props.bookingPage.title,
    description: props.bookingPage.description ?? "",
    avatar: props.bookingPage.avatar ?? "",
});

const canManage = computed(
    () =>
        props.bookingPage.capabilities.edit &&
        props.bookingPage.capabilities.manage,
);
const selectedAppearance = computed(() =>
    props.appearances.find(
        (appearance) => appearance.id === props.bookingPage.appearance_id,
    ),
);
const autosave = useDebounceFn(() => {
    if (!canManage.value) return;

    saveStatus.value = "saving";
    form.put(
        route("app.event-type-groups.update", {
            eventTypeGroup: props.bookingPage.id,
        }),
        {
            preserveScroll: true,
            preserveState: true,
            onSuccess: () => {
                saveStatus.value = "saved";
                window.setTimeout(() => {
                    if (saveStatus.value === "saved") saveStatus.value = "idle";
                }, 2500);
            },
            onError: () => {
                saveStatus.value = "error";
            },
        },
    );
}, 600);

watch(
    () => [form.title, form.description, form.avatar],
    () => {
        closeDesktopPreview();
        autosave();
    },
);

function stateLabel(state: BookingPageState): string {
    return {
        active: t("my_page.state.active"),
        needs_setup: t("my_page.state.needs_setup"),
        no_active_event_types: t("my_page.state.no_active_event_types"),
    }[state];
}

function stateDescription(state: BookingPageState): string {
    return {
        active: t("my_page.state.active_description"),
        needs_setup: t("my_page.state.needs_setup_description"),
        no_active_event_types: t(
            "my_page.state.no_active_event_types_description",
        ),
    }[state];
}

function stateClasses(state: BookingPageState): string {
    return {
        active: "border-green-200 bg-green-50 text-green-700",
        needs_setup: "border-amber-200 bg-amber-50 text-amber-700",
        no_active_event_types: "border-orange-200 bg-orange-50 text-orange-700",
    }[state];
}

function roleLabel(): string {
    return {
        owner: t("my_page.roles.owner"),
        admin: t("my_page.roles.admin"),
        editor: t("my_page.roles.editor"),
        viewer: t("my_page.roles.viewer"),
    }[props.bookingPage.role];
}

const pageHeading = computed(() =>
    props.bookingPage.team.personal_team
        ? props.bookingPage.title || props.bookingPage.team.name
        : props.bookingPage.team.name,
);

function onAppearanceSelected(appearance: IAppearance): void {
    if (!canManage.value) return;

    closeDesktopPreview();
    saveStatus.value = "saving";
    router.post(
        route("app.event-type-groups.appearances.assign", {
            eventTypeGroup: props.bookingPage.id,
        }),
        { appearanceId: appearance.id },
        {
            preserveScroll: true,
            onSuccess: () => {
                saveStatus.value = "saved";
            },
            onError: () => {
                saveStatus.value = "error";
            },
        },
    );
}
</script>

<template>
    <AppLayout>
        <template #icon>
            <div class="gap-3 flex items-center">
                <Link :href="route('app.event-type-groups.show')">
                    <Button
                        variant="ghost"
                        size="icon"
                        :aria-label="$t('my_page.editor.back')"
                    >
                        <ArrowLeft :size="17" />
                    </Button>
                </Link>
                <LayoutPanelTop :size="24" class="text-iron-500" />
            </div>
        </template>
        <template #title>{{ pageHeading }}</template>
        <template #description>
            {{
                bookingPage.team.personal_team
                    ? $t("my_page.editor.personal_page")
                    : $t("my_page.editor.team_page")
            }}
            · {{ roleLabel() }}
        </template>
        <template #actions>
            <div class="gap-2 flex items-center">
                <Link
                    v-if="bookingPage.capabilities.manage_domains"
                    :href="
                        route('app.domains.index', {
                            tab: 'page',
                            team: bookingPage.team.id,
                        })
                    "
                >
                    <Button variant="outline" class="gap-2 whitespace-nowrap">
                        <Globe2 :size="16" />
                        <span class="sm:inline hidden">{{
                            $t("my_page.editor.domain")
                        }}</span>
                    </Button>
                </Link>
                <ShareHubGroupModal :event-type-group="bookingPage">
                    <Button variant="outline" class="gap-2 whitespace-nowrap">
                        <Share :size="16" />
                        <span class="sm:inline hidden">{{
                            $t("my_page.editor.share")
                        }}</span>
                    </Button>
                </ShareHubGroupModal>
                <a
                    :href="bookingPage.public_url"
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    <Button variant="outline" class="gap-2 whitespace-nowrap">
                        <ExternalLink :size="16" />
                        <span class="sm:inline hidden">{{
                            $t("my_page.editor.open_live_page")
                        }}</span>
                    </Button>
                </a>
            </div>
        </template>

        <div class="px-4 py-6 sm:px-6 sm:py-8">
            <div
                class="max-w-7xl gap-6 xl:grid-cols-[minmax(0,1fr)_420px] mx-auto grid"
            >
                <div class="min-w-0 gap-6 flex flex-col">
                    <div
                        v-if="bookingPage.state !== 'active'"
                        class="gap-1 rounded-xl p-4 grid border"
                        :class="stateClasses(bookingPage.state)"
                    >
                        <p class="text-sm font-semibold">
                            {{ stateLabel(bookingPage.state) }}
                        </p>
                        <p class="text-sm">
                            {{ stateDescription(bookingPage.state) }}
                        </p>
                    </div>
                    <div
                        v-if="
                            bookingPage.capabilities.edit &&
                            !bookingPage.capabilities.manage
                        "
                        class="gap-3 rounded-xl border-z-primary-100 bg-z-primary-50 p-4 sm:flex-row sm:items-center sm:justify-between flex flex-col border"
                    >
                        <div>
                            <p class="font-medium text-iron-800">
                                {{ $t("my_page.editor.requires_pro") }}
                            </p>
                            <p class="mt-1 text-sm text-iron-600">
                                {{
                                    $t(
                                        "my_page.editor.requires_pro_description",
                                    )
                                }}
                            </p>
                        </div>
                        <Link
                            :href="
                                route('app.billing', {
                                    preselectedplan: 'yearly',
                                })
                            "
                        >
                            <Button class="gap-2 whitespace-nowrap">
                                <PaidFeatureIcon />
                                {{ $t("my_page.editor.upgrade") }}
                            </Button>
                        </Link>
                    </div>

                    <Card id="page-details" class="shadow-none">
                        <CardHeader class="border-iron-100 border-b">
                            <div
                                class="gap-4 flex items-center justify-between"
                            >
                                <div>
                                    <CardTitle>{{
                                        $t("my_page.editor.details")
                                    }}</CardTitle>
                                    <p class="mt-1 text-sm text-iron-500">
                                        {{
                                            $t(
                                                "my_page.editor.details_description",
                                            )
                                        }}
                                    </p>
                                </div>
                                <p
                                    class="text-xs font-medium"
                                    :class="
                                        saveStatus === 'error'
                                            ? 'text-error-600'
                                            : 'text-iron-400'
                                    "
                                    aria-live="polite"
                                >
                                    <span v-if="saveStatus === 'saving'">{{
                                        $t("saving")
                                    }}</span>
                                    <span v-else-if="saveStatus === 'saved'">{{
                                        $t("saved")
                                    }}</span>
                                    <span v-else-if="saveStatus === 'error'">{{
                                        $t("save_failed")
                                    }}</span>
                                    <span v-else-if="canManage">{{
                                        $t("my_page.editor.autosaves")
                                    }}</span>
                                    <span v-else>{{
                                        $t("my_page.editor.read_only")
                                    }}</span>
                                </p>
                            </div>
                        </CardHeader>
                        <CardContent class="gap-5 pt-6 grid">
                            <div class="gap-2 grid">
                                <Label>{{ $t("my_page.editor.avatar") }}</Label>
                                <AvatarUpload
                                    v-if="canManage"
                                    :image-url="form.avatar"
                                    :upload-url="route('app.images.store')"
                                    @file-uploaded-json="
                                        (url) => (form.avatar = url)
                                    "
                                />
                                <div
                                    v-else
                                    class="h-20 w-20 bg-iron-100 flex items-center justify-center overflow-hidden rounded-full"
                                >
                                    <img
                                        v-if="form.avatar"
                                        :src="form.avatar"
                                        alt=""
                                        class="h-full w-full object-cover"
                                    />
                                </div>
                            </div>
                            <div class="gap-2 grid">
                                <Label for="booking-page-title">{{
                                    $t("my_page.editor.title")
                                }}</Label>
                                <Input
                                    id="booking-page-title"
                                    v-model="form.title"
                                    :disabled="!canManage"
                                    maxlength="255"
                                />
                                <p
                                    v-if="form.errors.title"
                                    class="text-xs text-error-600"
                                >
                                    {{ form.errors.title }}
                                </p>
                            </div>
                            <div class="gap-2 grid">
                                <Label for="booking-page-description">{{
                                    $t("my_page.editor.description")
                                }}</Label>
                                <Textarea
                                    id="booking-page-description"
                                    v-model="form.description"
                                    :disabled="!canManage"
                                    rows="4"
                                    maxlength="5000"
                                />
                            </div>
                            <div class="gap-2 grid justify-items-start">
                                <Label>{{
                                    $t("my_page.editor.appearance")
                                }}</Label>
                                <AppearancePicker
                                    v-if="canManage"
                                    :selected-appearance="selectedAppearance"
                                    :appearances="appearances"
                                    @on-selected="onAppearanceSelected"
                                />
                                <p v-else class="text-sm text-iron-600">
                                    {{
                                        selectedAppearance?.name ??
                                        $t("my_page.editor.default_appearance")
                                    }}
                                </p>
                            </div>
                        </CardContent>
                    </Card>

                    <Card id="event-types" class="scroll-mt-4 shadow-none">
                        <CardHeader class="border-iron-100 border-b">
                            <CardTitle>{{
                                $t("my_page.editor.event_types")
                            }}</CardTitle>
                            <p class="text-sm text-iron-500">
                                {{
                                    $t("my_page.editor.event_types_description")
                                }}
                            </p>
                        </CardHeader>
                        <CardContent class="pt-6">
                            <EventTypesConfiguration
                                :event-types="eventTypes"
                                :booking-page="bookingPage"
                                :can-edit="canManage"
                                :can-create="
                                    bookingPage.capabilities.create_event_type
                                "
                                :highlighted-event-type-id="
                                    highlightedEventTypeId
                                "
                            />
                        </CardContent>
                    </Card>
                </div>

                <aside class="xl:sticky xl:top-6 xl:self-start">
                    <div
                        ref="previewCard"
                        class="rounded-xl border-iron-200 bg-iron-100 overflow-hidden border"
                        :class="{ invisible: desktopPreviewOpen }"
                    >
                        <div
                            class="border-iron-200 px-4 py-3 flex items-center justify-between border-b"
                        >
                            <div>
                                <p class="text-sm font-semibold text-iron-800">
                                    {{ $t("my_page.editor.preview") }}
                                </p>
                                <p class="text-xs text-iron-500">
                                    {{
                                        $t("my_page.editor.preview_description")
                                    }}
                                </p>
                            </div>
                            <div
                                class="rounded-lg bg-white p-1 ring-iron-200 flex ring-1"
                            >
                                <button
                                    type="button"
                                    class="rounded-md p-1.5 text-iron-400"
                                    :class="{
                                        'bg-iron-100 text-iron-700':
                                            desktopPreviewOpen,
                                    }"
                                    :aria-label="
                                        $t('my_page.editor.desktop_preview')
                                    "
                                    @click="openDesktopPreview"
                                >
                                    <Monitor :size="15" />
                                </button>
                                <button
                                    type="button"
                                    class="rounded-md p-1.5 text-iron-400"
                                    :class="{
                                        'bg-iron-100 text-iron-700':
                                            !desktopPreviewOpen,
                                    }"
                                    :aria-label="
                                        $t('my_page.editor.mobile_preview')
                                    "
                                    @click="closeDesktopPreview"
                                >
                                    <Smartphone :size="15" />
                                </button>
                            </div>
                        </div>
                        <div class="p-4 flex min-h-[560px] justify-center">
                            <BookingPagePreview
                                :booking-page="bookingPage"
                                mobile
                            />
                        </div>
                    </div>
                </aside>

                <Teleport to="body">
                    <div v-if="desktopPreviewOpen" class="inset-0 fixed z-50">
                        <div
                            class="bg-black/40 inset-0 absolute transition-opacity duration-300"
                            :class="
                                desktopPreviewExpanded
                                    ? 'opacity-100'
                                    : 'opacity-0'
                            "
                            @click="closeDesktopPreview"
                        />
                        <div
                            ref="expandedPanel"
                            class="rounded-xl border-iron-200 bg-iron-100 shadow-2xl ease-out absolute top-[5vh] right-[max(2.5vw,calc(50vw-36rem))] bottom-[5vh] left-[max(2.5vw,calc(50vw-36rem))] flex origin-top-left flex-col overflow-hidden border transition-[transform,opacity] duration-300"
                            :class="
                                desktopPreviewExpanded
                                    ? 'opacity-100'
                                    : 'opacity-90'
                            "
                            :style="{ transform: desktopPanelTransform }"
                        >
                            <div
                                class="border-iron-200 px-4 py-3 flex items-center justify-between border-b"
                            >
                                <div>
                                    <p
                                        class="text-sm font-semibold text-iron-800"
                                    >
                                        {{ $t("my_page.editor.preview") }}
                                    </p>
                                    <p class="text-xs text-iron-500">
                                        {{
                                            $t(
                                                "my_page.editor.preview_description",
                                            )
                                        }}
                                    </p>
                                </div>
                                <div
                                    class="rounded-lg bg-white p-1 ring-iron-200 flex ring-1"
                                >
                                    <button
                                        type="button"
                                        class="rounded-md p-1.5 bg-iron-100 text-iron-700"
                                        :aria-label="
                                            $t('my_page.editor.desktop_preview')
                                        "
                                    >
                                        <Monitor :size="15" />
                                    </button>
                                    <button
                                        type="button"
                                        class="rounded-md p-1.5 text-iron-400"
                                        :aria-label="
                                            $t('my_page.editor.mobile_preview')
                                        "
                                        @click="closeDesktopPreview"
                                    >
                                        <Smartphone :size="15" />
                                    </button>
                                </div>
                            </div>
                            <div
                                class="p-4 min-h-0 flex grow justify-center overflow-y-auto"
                            >
                                <BookingPagePreview
                                    :booking-page="bookingPage"
                                    :mobile="false"
                                />
                            </div>
                        </div>
                    </div>
                </Teleport>
            </div>
        </div>
    </AppLayout>
</template>
